import { HttpRequest } from '../utils/httpClient';
import { Permission } from '../features/auth/types';

import { createLogger } from '../utils/logger';
const logger = createLogger('services');

/**
 * Response from the permissions API (legacy endpoint)
 */
interface PermissionsResponse {
  permissions: Permission[];
}

/**
 * Resource with permission info from the role endpoint
 */
interface ResourceWithPermission {
  id: string;
  name: string;
  permission: string;
}

/**
 * Response from the role permissions endpoint (optimized)
 */
interface RolePermissionsResponse {
  message: string;
  role: {
    id: string;
    name: string;
    resources: ResourceWithPermission[];
  };
}

/**
 * Service for fetching user permissions from the backend API
 */
class PermissionService {
  private httpClient: HttpRequest;

  constructor() {
    const API_BASE_URL = process.env.REACT_APP_API_HOST || 'https://api.dev.mind.creai.cloud';
    const permissionsBaseUrl = `${API_BASE_URL}/permissions`;
    this.httpClient = new HttpRequest(permissionsBaseUrl);
  }

  /**
   * Fetch all permissions for a specific role WITH resource names (OPTIMIZED)
   *
   * This method uses the /permissions/role/{roleId}/permissions endpoint which returns
   * permissions already joined with resource names, eliminating the need for a
   * separate GET /resource call.
   *
   * @param roleId - The UUID of the role
   * @returns Array of permissions with resource names
   *
   * @example
   * ```typescript
   * const permissions = await permissionService.getRolePermissionsWithNames('db8d9bc4-2b53-4c29-8c5a-8e2f9d6f1f24');
   * // Returns: [
   * //   { id: "uuid", roleId: "...", resourceId: "...", resourceName: "/home", permission: "edit", status: "active" }
   * // ]
   * ```
   */
  async getRolePermissionsWithNames(roleId: string): Promise<Permission[]> {
    try {
      const response = await this.httpClient.get(`role/${roleId}/permissions`);

      if (response.status !== 200) {
        logger.error(`Failed to fetch role permissions: ${response.status} - ${response.statusText}`);
        return [];
      }

      const data = response.data as RolePermissionsResponse;
      const role = data.role;

      if (!role || !role.resources) {
        logger.warn('Role has no resources');
        return [];
      }

      // Transform resources to Permission format with resourceName
      const permissions: Permission[] = role.resources.map((resource) => ({
        id: `${roleId}-${resource.id}`, // Generate a composite ID
        roleId: roleId,
        resourceId: resource.id,
        resourceName: resource.name, // ✅ Include resource name
        permission: resource.permission,
        status: 'active' // Assumed active if returned by API
      }));

      return permissions;
    } catch (error) {
      logger.error('Error fetching role permissions with names:', error);
      return [];
    }
  }

  /**
   * Fetch all permissions for a specific role (LEGACY)
   *
   * @deprecated Use getRolePermissionsWithNames() instead for better performance
   *
   * @param roleId - The UUID of the role
   * @returns Array of permissions for the role
   *
   * @example
   * ```typescript
   * const permissions = await permissionService.getPermissionsByRole('db8d9bc4-2b53-4c29-8c5a-8e2f9d6f1f24');
   * ```
   */
  async getPermissionsByRole(roleId: string): Promise<Permission[]> {
    try {
      const response = await this.httpClient.get(`permission/${roleId}`);

      if (response.status !== 200) {
        logger.error(`Failed to fetch permissions: ${response.status} - ${response.statusText}`);
        return [];
      }

      // Backend returns { permissions: [...] }
      const data = response.data as PermissionsResponse;
      return data.permissions || [];
    } catch (error) {
      logger.error('Error fetching permissions:', error);
      return [];
    }
  }

  /**
   * Check if a role has access to a specific resource
   *
   * @param roleId - The UUID of the role
   * @param resourceId - The UUID of the resource
   * @returns Object with access status and permission level
   *
   * @example
   * ```typescript
   * const result = await permissionService.checkPermission(
   *   'db8d9bc4-2b53-4c29-8c5a-8e2f9d6f1f24',
   *   '38939911-e693-4516-9343-204d3d5820e8'
   * );
   * // Returns: { access: true, permission: 'edit' }
   * ```
   */
  async checkPermission(
    roleId: string,
    resourceId: string
  ): Promise<{ access: boolean; permission: string | null }> {
    try {
      const response = await this.httpClient.get(`permission/${roleId}/${resourceId}`);

      if (response.status !== 200) {
        return { access: false, permission: null };
      }

      return response.data as { access: boolean; permission: string | null };
    } catch (error) {
      logger.error('Error checking permission:', error);
      return { access: false, permission: null };
    }
  }
}

// Export singleton instance
export const permissionService = new PermissionService();
